<script lang="ts">
  import GridLines from '$lib/components/GridLines.svelte';
  import GridContent from '$lib/components/GridContent.svelte';
  import GridInner from '$lib/components/GridInner.svelte';
  import BlogCard from '$lib/components/BlogCard.svelte';
  import Star from '$lib/components/Star.svelte';
  import { featuredPosts, regularPosts } from '$lib/data/posts';
  import { hatchStyle45 } from '$lib/styles/hatch';
  import { submitNewsletter, LeadApiError } from '$lib/api/leads';

  type FormState = 'idle' | 'submitting' | 'success' | 'error';

  let email = $state('');
  let formState = $state<FormState>('idle');
  let errorMessage = $state('');

  async function handleSubscribe(e: Event) {
    e.preventDefault();

    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      formState = 'error';
      errorMessage = 'Please enter a valid email address';
      return;
    }

    formState = 'submitting';
    errorMessage = '';

    try {
      await submitNewsletter(trimmedEmail);
      formState = 'success';
      email = '';
    } catch (err) {
      formState = 'error';
      if (err instanceof LeadApiError) {
        errorMessage = err.message;
      } else {
        errorMessage = 'Something went wrong. Please try again.';
      }
    }
  }

  function resetForm() {
    formState = 'idle';
    errorMessage = '';
  }

  // RSS copy to clipboard
  let rssCopied = $state(false);

  async function copyRssUrl() {
    const rssUrl = `${window.location.origin}/rss.xml`;
    await navigator.clipboard.writeText(rssUrl);
    rssCopied = true;
    setTimeout(() => {
      rssCopied = false;
    }, 2000);
  }
</script>

<svelte:head>
  <title>News - Corral AI</title>
  <meta name="description" content="Updates, announcements, and stories from the Corral AI team." />
</svelte:head>

<!-- Hero Section -->
<section
  class="bg-[#f9f9f4] pt-16 pb-24 relative"
  style={hatchStyle45}
>
  <GridLines variant="light" />
  <GridContent>
    <GridInner>
      <div class="flex flex-col items-center text-center max-w-[700px] mx-auto">
        <!-- Eyebrow -->
        <p class="font-mono text-[14px] text-[#ee6018] uppercase tracking-wide mb-4">
          Latest Updates
        </p>

        <!-- Title -->
        <h1 class="font-display text-[48px] lg:text-[64px] text-[#020202] tracking-[-0.04em] leading-[1] mb-6">
          News & Insights
        </h1>

        <!-- Subtitle -->
        <p class="font-body text-[16px] lg:text-[18px] text-[#5C5855] leading-relaxed">
          Product announcements, engineering deep-dives, and perspectives on enterprise AI from the Corral team.
        </p>

        <!-- RSS Copy Button -->
        <div class="relative inline-block mt-6">
          <button
            type="button"
            onclick={copyRssUrl}
            class="inline-flex items-center gap-2 font-mono text-[13px] text-[#5C5855] hover:text-[#ee6018] transition-colors cursor-pointer"
          >
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 11a9 9 0 0 1 9 9" />
              <path d="M4 4a16 16 0 0 1 16 16" />
              <circle cx="5" cy="19" r="1" />
            </svg>
            RSS Feed
          </button>

          {#if rssCopied}
            <span
              class="absolute left-1/2 -translate-x-1/2 -top-10 px-3 py-1.5 bg-[#020202] text-white text-[11px] font-mono rounded whitespace-nowrap animate-in fade-in slide-in-from-bottom-1 duration-150"
            >
              Copied RSS feed URL to clipboard. Happy reading!
            </span>
          {/if}
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Featured Posts Section -->
{#if featuredPosts.length > 0}
  <section class="bg-[#0d0d0d] relative">
    <GridLines variant="dark" />

    <!-- Top border with stars (stars offset 1px down to align points with line) -->
    <div class="relative">
      <div class="hidden lg:block absolute top-0 left-0 right-0 h-px bg-[#333335]" aria-hidden="true"></div>
      <Star variant="bottom" class="hidden lg:block absolute left-[calc(9.03%-9px)] top-0" />
      <Star variant="bottom" class="hidden lg:block absolute right-[calc(9.03%-9px)] top-0" />
    </div>

    <GridContent class="py-16 lg:py-20">
      <GridInner>
        <!-- Section label -->
        <div class="flex items-center gap-3 mb-8">
          <span class="font-mono text-[12px] text-[#ee6018] uppercase tracking-wider">Featured</span>
          <div class="flex-1 h-px bg-[#333335]"></div>
        </div>

        <!-- Featured grid -->
        <div class="grid md:grid-cols-2 gap-6 lg:gap-8">
          {#each featuredPosts as post}
            <BlogCard {...post} featured={true} />
          {/each}
        </div>
      </GridInner>
    </GridContent>
  </section>
{/if}

<!-- All Posts Section -->
<section class="bg-[#0d0d0d] relative">
  <GridLines variant="dark" />

  <!-- Divider with stars -->
  <div class="relative">
    <div class="hidden lg:block absolute top-0 left-0 right-0 h-px bg-[#333335]" aria-hidden="true"></div>
    <Star variant="full" class="hidden lg:block absolute left-[calc(9.03%-9px)] -top-[9px]" />
    <Star variant="full" class="hidden lg:block absolute right-[calc(9.03%-9px)] -top-[9px]" />
  </div>

  <GridContent class="py-16 lg:py-20">
    <GridInner>
      <!-- Section label -->
      <div class="flex items-center gap-3 mb-8">
        <span class="font-mono text-[12px] text-[#878787] uppercase tracking-wider">All Posts</span>
        <div class="flex-1 h-px bg-[#333335]"></div>
      </div>

      <!-- Posts grid -->
      <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {#each regularPosts as post}
          <BlogCard {...post} />
        {/each}
      </div>

      <!-- Empty state -->
      {#if regularPosts.length === 0 && featuredPosts.length === 0}
        <div class="text-center py-16">
          <p class="font-mono text-[14px] text-[#878787]">
            No posts yet. Check back soon.
          </p>
        </div>
      {/if}
    </GridInner>
  </GridContent>

  <!-- Bottom border with stars -->
  <div class="relative">
    <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#333335]" aria-hidden="true"></div>
    <Star variant="top" class="hidden lg:block absolute left-[calc(9.03%-9px)] -bottom-px" />
    <Star variant="top" class="hidden lg:block absolute right-[calc(9.03%-9px)] -bottom-px" />
  </div>
</section>

<!-- Subscribe CTA Section -->
<section class="bg-[#0d0d0d] py-20 relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="max-w-[600px] mx-auto text-center">
        <h2 class="font-display text-[28px] lg:text-[36px] text-white tracking-[-0.02em] mb-4">
          Stay Updated
        </h2>
        <p class="font-body text-[16px] text-[#878787] mb-8">
          Get notified when we publish new articles about enterprise AI, security architecture, and product updates.
        </p>

        {#if formState === 'success'}
          <!-- Success State -->
          <div class="max-w-[400px] mx-auto">
            <p class="font-mono text-[14px] text-[#ee6018] mb-4">
              You're subscribed!
            </p>
            <button
              type="button"
              onclick={resetForm}
              class="font-mono text-[12px] text-[#878787] hover:text-white underline transition-colors"
            >
              Subscribe another email
            </button>
          </div>
        {:else}
          <!-- Email signup form -->
          <form onsubmit={handleSubscribe} class="flex flex-col sm:flex-row gap-3 max-w-[400px] mx-auto">
            <input
              type="email"
              bind:value={email}
              placeholder="you@company.com"
              disabled={formState === 'submitting'}
              class="flex-1 bg-[#171719] border border-[#333335] px-4 py-3 font-mono text-[14px] text-white placeholder:text-[#5C5855] focus:outline-none focus:border-[#ee6018] transition-colors disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={formState === 'submitting'}
              class="bg-[#ee6018] text-white font-mono text-[14px] px-6 py-3 hover:bg-[#d55515] transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {formState === 'submitting' ? 'Subscribing...' : 'Subscribe'}
            </button>
          </form>

          {#if formState === 'error' && errorMessage}
            <p class="font-mono text-[12px] text-red-400 mt-3">
              {errorMessage}
            </p>
          {:else}
            <p class="font-mono text-[12px] text-[#5C5855] mt-4">
              No spam. Unsubscribe anytime.
            </p>
          {/if}
        {/if}
      </div>
    </GridInner>
  </GridContent>
</section>
